// Aktif Etsy magazasi secici. Coklu magaza (POD'da farkli nis = farkli magaza)
// icin tum Etsy sayfalari bu secime gore veri gosterir.

import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { ChevronDown, Check, Plus, AlertCircle } from "lucide-react";
import { clsx } from "clsx";
import { useShopStore } from "../../store/shop.store";

const DOT_FALLBACK = "#6366f1";

/** Magaza degisince uzerinde KALINABILECEK sayfalar. Digerleri (detay
 *  sayfalari, dikeye ozel uretim ekranlari) yeni magazada anlamsiz veya bos
 *  kalir; oradan Dashboard'a doneriz. */
const SAFE_PATHS = ["/dashboard", "/settings", "/my-shops", "/plans"];

export function ShopSwitcher() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const { shops, activeShopId, loadShops, setActiveShop } = useShopStore();
  const [open, setOpen] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);

  useEffect(() => { loadShops(); }, [loadShops]);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  // Hic magaza yoksa secici gosterme — Ayarlar'dan baglama cagrisi yap
  if (shops.length === 0) {
    return (
      <Link
        to="/settings"
        className="flex items-center gap-1.5 rounded-lg border border-dashed border-gray-300 px-2.5 py-1.5 text-xs text-gray-500 hover:border-indigo-400 hover:text-indigo-600"
      >
        <Plus className="h-3.5 w-3.5" /> Mağaza bağla
      </Link>
    );
  }

  const active = shops.find((s) => s.id === activeShopId);
  const activeShops = shops.filter((s) => s.isActive);
  const passiveShops = shops.filter((s) => !s.isActive);

  function choose(id: string) {
    if (id === activeShopId) { setOpen(false); return; }
    setActiveShop(id);
    setOpen(false);
    // Magaza degisti: tum sunucu verisi yeniden cekilmeli
    queryClient.invalidateQueries();
    // Acik sayfa yeni magazada gecerli degilse Dashboard'a don. Onceki
    // magazanin sablon/urun listesiyle kalmak hataya ve yanlis kayda yol
    // aciyordu (or. sablon sayfasinda pazaryeri degisince istek patliyordu).
    const path = location.pathname;
    const safe = SAFE_PATHS.some((p) => path === p || path.startsWith(`${p}/`));
    if (!safe) navigate("/dashboard");
  }

  return (
    <div className="relative" ref={boxRef}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 rounded-lg border border-gray-200 px-2.5 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
        title="Aktif Etsy mağazası"
      >
        <span
          className="h-2.5 w-2.5 shrink-0 rounded-full"
          style={{ background: active?.color || DOT_FALLBACK }}
        />
        <span className="max-w-[160px] truncate font-medium">
          {active?.shopName || "Mağaza seç"}
        </span>
        {active?.tokenExpired && <AlertCircle className="h-3.5 w-3.5 text-amber-500" />}
        <ChevronDown className="h-3.5 w-3.5 text-gray-400" />
      </button>

      {open && (
        <div className="absolute right-0 z-50 mt-1 w-72 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-lg">
          <div className="max-h-80 overflow-y-auto py-1">
            {activeShops.map((shop) => (
              <button
                key={shop.id}
                onClick={() => choose(shop.id)}
                className="flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-gray-50"
              >
                <span
                  className="h-2.5 w-2.5 shrink-0 rounded-full"
                  style={{ background: shop.color || DOT_FALLBACK }}
                />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm text-gray-900">{shop.shopName}</span>
                  <span className="block truncate text-[11px] text-gray-400">
                    {shop.label || `#${shop.externalShopId}`}
                  </span>
                </span>
                {shop.tokenExpired && (
                  <span title="Token süresi dolmuş"><AlertCircle className="h-3.5 w-3.5 text-amber-500" /></span>
                )}
                {shop.id === activeShopId && <Check className="h-4 w-4 text-indigo-600" />}
              </button>
            ))}

            {passiveShops.length > 0 && (
              <>
                <p className="mt-1 border-t border-gray-100 px-3 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-wide text-gray-400">
                  Pasif
                </p>
                {passiveShops.map((shop) => (
                  <div
                    key={shop.id}
                    className={clsx("flex items-center gap-2 px-3 py-1.5 text-sm text-gray-400")}
                    title="Pasif mağaza — Ayarlar'dan aktif edebilirsiniz"
                  >
                    <span className="h-2.5 w-2.5 shrink-0 rounded-full bg-gray-300" />
                    <span className="min-w-0 flex-1 truncate">{shop.shopName}</span>
                  </div>
                ))}
              </>
            )}
          </div>

          <Link
            to="/settings"
            onClick={() => setOpen(false)}
            className="flex items-center gap-1.5 border-t border-gray-100 px-3 py-2 text-xs text-indigo-600 hover:bg-indigo-50"
          >
            <Plus className="h-3.5 w-3.5" /> Mağaza ekle / yönet
          </Link>
        </div>
      )}
    </div>
  );
}
